// CustomDropdown.tsx
import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

// 再利用可能なスタイルを定義
const DropdownContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  width: '100%',
}));

const DropdownTrigger = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  cursor: 'pointer',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
}));

const DropdownList = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  zIndex: 10,
  marginTop: theme.spacing(0.5),
  borderRadius: 12,
  maxHeight: 300,
  overflow: 'auto',
  overscrollBehavior: 'contain', // スクロールの挙動を制御
}));

const StyledListItem = styled(ListItem)<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(88, 86, 214, 0.08)', // Re:crewの青紫色（薄め）
  },
  ...(selected && {
    backgroundColor: 'rgba(88, 86, 214, 0.12)', // Re:crewの青紫色（選択時）
  }),
}));

interface CustomDropdownProps {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  icon?: React.ReactNode;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  icon,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // リスト外クリックを検知するためのイベントリスナーを設定
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    // リスナーを追加
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // クリーンアップ
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setOpen(false);
  };

  // 現在選択されている項目のラベルを取得
  const selectedLabel = options.find(option => option.value === value)?.label || label;

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownTrigger onClick={handleToggle}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon}
          <Typography 
            variant="body1" 
            sx={{ 
              ml: 1.5, 
              fontWeight: 500,
              color: '#333',
            }}
          >
            {selectedLabel}
          </Typography>
        </Box>
        <IconButton 
          size="small" 
          sx={{ 
            p: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </DropdownTrigger>

      <Collapse in={open} sx={{ position: 'absolute', width: '100%', zIndex: 1000 }}>
        <DropdownList elevation={3}>
          <List sx={{ py: 0.5, maxHeight: 250, overflowY: 'auto' }}>
            {options.map((option) => (
              <StyledListItem 
                key={option.value}
                onClick={() => handleSelect(option.value)}
                selected={value === option.value}
              >
                <ListItemText 
                  primary={option.label}
                  primaryTypographyProps={{
                    sx: { 
                      fontWeight: value === option.value ? 600 : 400,
                      color: value === option.value ? '#5856D6' : '#333',
                    }
                  }}
                />
              </StyledListItem>
            ))}
          </List>
        </DropdownList>
      </Collapse>
    </DropdownContainer>
  );
};

export default CustomDropdown;